import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { ContaResponseDTO } from '../../models/gerenciar-contas/conta.model';
import { PageNotaConsultaDTO } from '../../models/nota.model';
import { NfeEventoEnvioDTO } from '../../models/nfe.model';
import { ExtratoDeCaixaPdfParams } from '../../models/movimento/extrato-de-caixa-pdf.model';
import {
  ConfiguracaoEmpresaContadorConsultaDTO,
  ConfiguracaoEmpresaContadorDTO,
  VendedorVendaDTO,
} from '../../models/contador/configuracao-empresa-contador.model';
import { TipoPadraoNota } from '../../enums/TipoPadraoNota.enum';

/**
 * Service da feature "Operar Cliente" (Area do Contador).
 *
 * Reusa endpoints EXISTENTES do backend; quem opera no cliente e o header
 * `X-Empresa-Contexto`, injetado pelo `ContadorContextoInterceptor` (este
 * service NAO conhece o header). Todos os endpoints aqui estao no allowlist:
 * GET /conta, GET /relatorios/relatorio-extrato-de-caixa, GET /nota,
 * POST /nfe/transmitir/{id}.
 *
 * Fonte da verdade: `vendas/docs/api/API_CONTRACT_contador-operar-cliente.md`.
 */
@Injectable({ providedIn: 'root' })
export class ContadorOperarService {
  constructor(private apiService: ApiService) {}

  /** GET /conta — contas do cliente (para escolher a conta do extrato). */
  listarContas(page = 0, size = 50): Observable<ContaResponseDTO> {
    return this.apiService.read<ContaResponseDTO>(`/conta?page=${page}&size=${size}`);
  }

  /**
   * GET /relatorios/relatorio-extrato-de-caixa — PDF base64 do extrato do cliente.
   * Segue o mesmo padrao de BaixaService.exportarExtratoDeCaixaPdf (readText).
   */
  gerarExtratoPdf(params: ExtratoDeCaixaPdfParams): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(params as any);
    const path = queryParams
      ? `/relatorios/relatorio-extrato-de-caixa?${queryParams}`
      : '/relatorios/relatorio-extrato-de-caixa';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET /nota/buscar — notas do cliente: apenas VENDA com fatura legal,
   * filtráveis por período (dataInicio/dataFim) e por estado da nota eletronica
   * (estadoNotaEletronica) ou pelo flag semEstadoEletronico (notas sem estado/NULL).
   * Cada filtro so vai no params quando definido.
   */
  listarNotas(
    page = 0,
    size = 20,
    dataInicio?: string,
    dataFim?: string,
    estadoNotaEletronica?: string,
    semEstadoEletronico?: boolean,
    sort?: string,
    tipoNota?: TipoPadraoNota,
  ): Observable<PageNotaConsultaDTO> {
    const params: Record<string, unknown> = {
      tipoDeTransacao: 'VENDA',
      ehFaturaLegal: true,
      page,
      size,
    };
    if (dataInicio) {
      params['dataInicio'] = dataInicio;
    }
    if (dataFim) {
      params['dataFim'] = dataFim;
    }
    if (estadoNotaEletronica) {
      params['estadoNotaEletronica'] = estadoNotaEletronica;
    }
    if (semEstadoEletronico) {
      params['semEstadoEletronico'] = true;
    }
    // Filtro "Nao emitida": tipoNota=ELETRONICA combinado com semEstadoEletronico
    // restringe as notas sem estado as do tipo eletronico (ainda nao transmitidas).
    if (tipoNota) {
      params['tipoNota'] = tipoNota;
    }
    // sort no formato Spring Pageable ("campo,asc|desc"). So vai quando o
    // usuario clicou num cabecalho ordenavel; ausente => ordenacao default.
    if (sort) {
      params['sort'] = sort;
    }
    const queryParams = this.apiService.buildQueryParams(params);
    return this.apiService.read<PageNotaConsultaDTO>(`/nota/buscar?${queryParams}`);
  }

  /**
   * GET /relatorios/livro-de-vendas — PDF base64 do "Libro de Ventas" do cliente.
   * Espelha gerarExtratoPdf: respeita o contexto (X-Empresa-Contexto via interceptor)
   * e os filtros ativos (periodo + estado da nota eletronica). Retorna base64;
   * string vazia = sem dados (o componente trata com toast "sem dados").
   */
  gerarLivroDeVendasPdf(params: {
    dataInicio?: string;
    dataFim?: string;
    estadoNotaEletronica?: string;
    semEstadoEletronico?: boolean;
  }): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(params as any);
    const path = queryParams
      ? `/relatorios/livro-de-vendas?${queryParams}`
      : '/relatorios/livro-de-vendas';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET /relatorios/livro-de-vendas?...&formato=xlsx|csv — exporta o "Libro de
   * Ventas" do cliente como arquivo binario (.xlsx ou .csv) para download.
   * Mesmo endpoint/filtros do PDF (periodo + estado da nota eletronica); o que
   * muda e o `formato`. Diferente de `gerarLivroDeVendasPdf` (base64 via
   * readText), aqui o corpo e binario (Blob via readBlob); body vazio
   * (blob.size === 0) = "sem dados". Respeita o contexto (X-Empresa-Contexto).
   */
  gerarLivroDeVendasArquivo(
    formato: 'xlsx' | 'csv',
    params: {
      dataInicio?: string;
      dataFim?: string;
      estadoNotaEletronica?: string;
      semEstadoEletronico?: boolean;
    },
  ): Observable<Blob> {
    const queryParams = this.apiService.buildQueryParams({ ...params, formato } as any);
    return this.apiService.readBlob(`/relatorios/livro-de-vendas?${queryParams}`);
  }

  /** POST /nfe/transmitir/{id} — emite (transmite) a NFe de uma nota do cliente. */
  transmitirNfe(notaId: number): Observable<NfeEventoEnvioDTO> {
    return this.apiService.create<NfeEventoEnvioDTO>(`/nfe/transmitir/${notaId}`, undefined);
  }

  /**
   * GET /configuracaoEmpresa/contador — subconjunto da configuracao da empresa
   * do cliente (resolvido pelo contexto no backend, sem id na URL).
   */
  buscarConfiguracao(): Observable<ConfiguracaoEmpresaContadorConsultaDTO> {
    return this.apiService.read<ConfiguracaoEmpresaContadorConsultaDTO>('/configuracaoEmpresa/contador');
  }

  /** PUT /configuracaoEmpresa/contador — altera o subconjunto editavel. */
  atualizarConfiguracao(
    dto: ConfiguracaoEmpresaContadorDTO,
  ): Observable<ConfiguracaoEmpresaContadorConsultaDTO> {
    return this.apiService.update<ConfiguracaoEmpresaContadorConsultaDTO>('/configuracaoEmpresa/contador', dto);
  }

  /**
   * GET /contador/vendedores-venda — vendedores da aba "Nova venda": usuários da
   * empresa do cliente + o contador (resolvido pelo contexto no backend).
   */
  listarVendedoresParaVenda(): Observable<VendedorVendaDTO[]> {
    return this.apiService.read<VendedorVendaDTO[]>('/contador/vendedores-venda');
  }
}
